import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Progress } from '../../components/ui/progress';
import {
  ArrowRight, ArrowLeft, CheckCircle2, Loader2, Sparkles,
  Brain, Flame, Heart, Zap, Trophy, Download, ExternalLink,
  Wrench, Megaphone, Eye, Shield,
  Crosshair, BookOpen, Crown, Users,
} from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../app/api';

// Audience-specific quiz data sets (each module exports ARCHETYPES, QUESTIONS,
// scoreQuiz, and REQUIRES_SPORT)
import * as athleteData from './quizData';
import * as parentData from './parentQuizData';
import * as coachData from './coachQuizData';

import { SPORTS, getScene } from './quizData';

// ---------- Audience config -------------------------------------------------
//
// Each audience plugs into the same quiz engine. The athlete quiz keeps the
// existing sport picker + sport-scoped scene library. Parent / coach quizzes
// are sport-agnostic and use a scene baked into each question.

const AUDIENCE_CONFIG = {
  athlete: {
    key: 'athlete',
    label: 'I\u2019m the athlete',
    sublabel: 'Diagnose your mental game',
    data: athleteData,
    pdf: '/coach-lewis-frameworks.pdf',
    pdfFilename: 'Apex-6-Mental-Frameworks.pdf',
    pdfCta: 'Download the full Apex Six PDF',
    emailHeading: 'Get your full profile + the playbook PDF',
    emailSub: "We'll email the framework PDF (all 6 tools — printable posters) plus your archetype recap. No spam. Unsubscribe anytime.",
  },
  parent: {
    key: 'parent',
    label: 'I\u2019m a parent',
    sublabel: 'Diagnose your parenting style',
    data: parentData,
    pdf: '/car-ride-home-playbook.pdf',
    pdfFilename: 'Car-Ride-Home-Playbook.pdf',
    pdfCta: 'Download the Car Ride Home Playbook',
    emailHeading: 'Get your full profile + the Parent Playbook',
    emailSub: "We'll email the Car Ride Home Playbook plus your parenting archetype recap. No spam. Unsubscribe anytime.",
  },
  coach: {
    key: 'coach',
    label: 'I\u2019m a coach',
    sublabel: 'Diagnose your coaching style',
    data: coachData,
    pdf: '/coaches-reset.pdf',
    pdfFilename: 'Coaches-Reset.pdf',
    pdfCta: "Download the Coach's Reset",
    emailHeading: 'Get your full profile + the Coach Playbook',
    emailSub: "We'll email the Coach's Reset plus your coaching archetype recap. No spam. Unsubscribe anytime.",
  },
};

const AUDIENCE_ORDER = ['athlete', 'parent', 'coach'];

const ARCHETYPE_ICONS = {
  // athletes
  grinder: Brain,
  hopeful: Heart,
  phoenix: Flame,
  natural: Zap,
  // parents
  fixer: Wrench,
  cheerleader: Megaphone,
  hoverer: Eye,
  stoic: Shield,
  // coaches
  drill_sergeant: Crosshair,
  mentor: BookOpen,
  tactician: Crown,
  players_coach: Users,
};

// ---------- Step components --------------------------------------------------

const StepIntro = ({ onStart }) => (
  <div className="text-center space-y-6" data-testid="quiz-intro">
    <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#004C54]/10 text-[#004C54] text-xs font-bold uppercase tracking-[0.16em]">
      <Sparkles className="h-3 w-3" /> Free · 2 minutes
    </div>
    <h1 className="font-heading text-4xl sm:text-6xl font-bold tracking-tight">
      What&rsquo;s your mental<br className="hidden sm:block" /> game gap?
    </h1>
    <p className="text-foreground/70 text-lg sm:text-xl max-w-xl mx-auto">
      Eight quick questions. One result. Discover which Coach Lewis framework to run first —
      whether you&rsquo;re the athlete, the parent in the stands, or the coach running the program.
    </p>
    <div className="pt-2">
      <Button
        onClick={onStart}
        size="lg"
        className="bg-[#004C54] hover:bg-[#003a40] text-white text-base font-semibold px-8 h-12"
        data-testid="quiz-start-btn"
      >
        Start the quiz <ArrowRight className="h-4 w-4 ml-2" />
      </Button>
    </div>
    <p className="text-xs text-foreground/55 pt-2">
      Built by Coach Lewis — M.A. counseling, 15+ years coaching at the club and HS level.
    </p>
  </div>
);

const StepAudience = ({ value, onChoose, onBack }) => (
  <div className="space-y-6" data-testid="quiz-audience">
    <div className="text-center">
      <div className="font-mono-accent uppercase tracking-[0.18em] text-[11px] font-bold text-[#004C54]">Step 1</div>
      <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Who is this for?</h2>
      <p className="text-foreground/65 mt-2">Each path has its own questions and its own playbook.</p>
    </div>
    <div className="grid gap-3">
      {AUDIENCE_ORDER.map((key) => {
        const a = AUDIENCE_CONFIG[key];
        return (
          <button
            key={key}
            onClick={() => onChoose(key)}
            className={`text-left p-5 rounded-xl border-2 transition-colors hover:border-[#004C54] hover:bg-[#004C54]/5
              ${value === key ? 'border-[#004C54] bg-[#004C54]/5' : 'border-foreground/15'}`}
            data-testid={`quiz-audience-${key}`}
          >
            <div className="font-heading text-lg font-bold">{a.label}</div>
            <div className="text-sm text-foreground/65 mt-1">{a.sublabel}</div>
          </button>
        );
      })}
    </div>
    <div className="flex justify-between pt-2">
      <Button onClick={onBack} variant="ghost" size="sm" data-testid="quiz-back">
        <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
      </Button>
    </div>
  </div>
);

const StepSport = ({ value, onChoose, onBack }) => (
  <div className="space-y-6" data-testid="quiz-sport">
    <div className="text-center">
      <div className="font-mono-accent uppercase tracking-[0.18em] text-[11px] font-bold text-[#004C54]">Step 2</div>
      <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Pick your sport</h2>
      <p className="text-foreground/65 mt-2">Scenarios in the quiz will match your game.</p>
    </div>
    <div className="grid grid-cols-2 gap-3">
      {SPORTS.map((s) => (
        <button
          key={s.key}
          onClick={() => onChoose(s.key)}
          className={`text-left p-4 rounded-xl border-2 transition-colors hover:border-[#004C54] hover:bg-[#004C54]/5
            ${value === s.key ? 'border-[#004C54] bg-[#004C54]/5' : 'border-foreground/15'}`}
          data-testid={`quiz-sport-${s.key}`}
        >
          <div className="font-heading font-semibold">{s.label}</div>
        </button>
      ))}
    </div>
    <div className="flex justify-between pt-2">
      <Button onClick={onBack} variant="ghost" size="sm" data-testid="quiz-back">
        <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
      </Button>
    </div>
  </div>
);

const StepQuestion = ({ index, total, scene, stem, options, onAnswer, onBack }) => (
  <div className="space-y-5" data-testid={`quiz-question-${index + 1}`}>
    <div>
      <div className="flex items-center justify-between mb-2">
        <div className="font-mono-accent uppercase tracking-[0.18em] text-[11px] font-bold text-[#004C54]">
          Question {index + 1} of {total}
        </div>
        <div className="text-xs text-foreground/60">{Math.round(((index + 1) / total) * 100)}%</div>
      </div>
      <Progress value={((index + 1) / total) * 100} className="h-2" />
    </div>
    <div className="p-5 rounded-xl bg-[#004C54]/8 border-l-4 border-[#004C54]">
      <div className="text-xs font-bold text-[#004C54] uppercase tracking-wider mb-2">The scene</div>
      <p className="text-base sm:text-lg leading-snug" data-testid="quiz-scene">{scene}</p>
    </div>
    <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">{stem}</h2>
    <div className="grid gap-3">
      {options.map((opt, i) => (
        <button
          key={`${opt.archetype}-${opt.text}`}
          onClick={() => onAnswer(opt.archetype)}
          className="text-left p-4 rounded-xl border-2 border-foreground/15 hover:border-[#004C54] hover:bg-[#004C54]/5 transition-colors"
          data-testid={`quiz-option-${i}`}
        >
          <div className="text-[15px] leading-snug">{opt.text}</div>
        </button>
      ))}
    </div>
    <div className="flex justify-between pt-2">
      <Button onClick={onBack} variant="ghost" size="sm" data-testid="quiz-back">
        <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
      </Button>
    </div>
  </div>
);

// ---------- Results Gate (NEW — Phase 33) -----------------------------------
//
// Sits between the last quiz question and the archetype reveal. Captures
// email + first name BEFORE the score is shown. This converts curious
// quiz-takers into Mental Edge Members at the moment of peak intent — they
// can't see their score until they opt in.
//
// Privacy: implicit consent shown below the button (no checkbox). Matches the
// pattern used on the AssessmentSales checkout flow.
const StepGate = ({ audience, submitting, onSubmit, onBack }) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');

  const submit = (e) => {
    e.preventDefault();
    if (!email.includes('@')) return toast.error('Please enter a valid email.');
    onSubmit({ email, name });
  };

  const audienceLabel =
    audience === 'parent' ? 'parenting' : audience === 'coach' ? 'coaching' : 'mindset';

  return (
    <div className="space-y-6" data-testid="quiz-results-gate">
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#004C54]/10 text-[#004C54] text-[11px] font-bold uppercase tracking-[0.18em]">
          <Sparkles className="h-3 w-3" /> Score ready
        </div>
        <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">
          Your Mental Edge Score is ready.
        </h2>
        <p className="text-foreground/65 text-base sm:text-lg max-w-md mx-auto">
          Where should we send your full {audienceLabel} profile + your custom 7-day action plan?
        </p>
      </div>

      <Card className="border-2 border-[#004C54]">
        <CardContent className="p-6 sm:p-7 space-y-4">
          <form onSubmit={submit} className="space-y-3" data-testid="quiz-gate-form">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your first name"
              data-testid="quiz-gate-name"
              autoComplete="given-name"
            />
            <Input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              data-testid="quiz-gate-email"
              autoComplete="email"
            />
            <Button
              type="submit"
              disabled={submitting}
              size="lg"
              className="w-full bg-[#004C54] hover:bg-[#003a40] text-white font-semibold h-12"
              data-testid="quiz-gate-submit"
            >
              {submitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Unlocking your results...</>
              ) : (
                <>Show me my results <ArrowRight className="h-4 w-4 ml-2" /></>
              )}
            </Button>
            <p className="text-[11.5px] text-foreground/55 text-center pt-1">
              By continuing you agree to receive your results + weekly mental-game emails from Coach Lewis. Unsubscribe anytime.
            </p>
          </form>
        </CardContent>
      </Card>

      <div className="flex justify-between pt-1">
        <Button onClick={onBack} variant="ghost" size="sm" data-testid="quiz-gate-back">
          <ArrowLeft className="h-4 w-4 mr-1.5" /> Back
        </Button>
      </div>
    </div>
  );
};

import { Calendar, Headphones, Target } from 'lucide-react';
import { CALENDLY_URL } from '../../lib/siteConfig';

// ---------- Next-step CTA after quiz result (Phase 21.2) -------------------
//
// Promotes the right paid next-step per audience at the moment of peak
// engagement (right after the archetype reveal). Layout: 2-column grid on
// desktop (primary CTA + Locker Room secondary), stacks on mobile.

/**
 * trackQuizCta — best-effort analytics ping for quiz CTA clicks. Mirrors the
 * pattern used in the Navbar so funnels stay consistent in GA / Segment.
 */
function trackQuizCta(label, audience) {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag('event', 'quiz_cta_click', { label, audience });
    }
  } catch (err) {
    if (typeof console !== 'undefined' && console.debug) {
      console.debug('[MindsetQuiz] analytics dispatch failed', err);
    }
  }
}

const PRIMARY_CTA_BY_AUDIENCE = {
  athlete: {
    eyebrow: 'Next step · $27',
    title: 'Get your full Apex Six IDP',
    body: 'A 6-dimension scored profile with your Primary Action, the script, and a printable PDF. Built for athletes who want to lock in what they just discovered.',
    cta: 'Start my IDP — $27',
    href: '/assessment',
    testid: 'quiz-next-idp',
    label: 'IDP',
    icon: Target,
  },
  parent: {
    eyebrow: 'Next step · 30 min',
    title: 'Talk it through with Coach Lewis',
    body: "Bring your specific car-ride-home scenarios. We'll map out the language, the rhythm, and the boundaries — built for your kid, your sport, your season.",
    cta: 'Book a Discovery Call',
    href: CALENDLY_URL,
    testid: 'quiz-next-discovery',
    label: 'Discovery Call',
    icon: Calendar,
    external: true,
  },
  coach: {
    eyebrow: 'Next step · team workshop',
    title: 'Bring the Apex Six to your roster',
    body: 'A 60–90 minute mental performance session for your team. Composure, confidence, and pressure-response — practical drills your players will use in their next game.',
    cta: 'Inquire about a Workshop',
    href: CALENDLY_URL,
    testid: 'quiz-next-workshop',
    label: 'Workshop',
    icon: Users,
    external: true,
  },
};

const NextStepCTA = ({ audience }) => {
  const primary = PRIMARY_CTA_BY_AUDIENCE[audience] || PRIMARY_CTA_BY_AUDIENCE.athlete;
  const PIcon = primary.icon;

  return (
    <div className="grid sm:grid-cols-[1.4fr_1fr] gap-3" data-testid="quiz-next-step-cta">
      <div className="rounded-2xl bg-[#0a0d0e] text-white p-5 sm:p-6 ring-1 ring-white/10 shadow-lg shadow-black/10">
        <div className="inline-flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#4FBCC7] font-bold mb-2">
          <PIcon className="h-3 w-3" />
          {primary.eyebrow}
        </div>
        <h3 className="font-heading text-[1.35rem] sm:text-[1.5rem] font-bold tracking-tight leading-[1.15] mb-2.5">
          {primary.title}
        </h3>
        <p className="text-[13.5px] text-white/70 leading-snug mb-5 max-w-[42ch]">
          {primary.body}
        </p>
        <a
          href={primary.href}
          {...(primary.external ? { target: '_blank', rel: 'noreferrer' } : {})}
          onClick={() => trackQuizCta(primary.label, audience)}
          data-testid={primary.testid}
        >
          <Button size="lg" className="bg-white text-[#0a0d0e] hover:bg-white/90 font-semibold w-full sm:w-auto h-11 px-5">
            {primary.cta}
            <ArrowRight className="h-4 w-4 ml-1.5" />
          </Button>
        </a>
      </div>

      <a
        href="/locker-room"
        onClick={() => trackQuizCta('Locker Room', audience)}
        data-testid="quiz-next-locker-room"
        className="group rounded-2xl bg-gradient-to-br from-[#004C54] to-[#003a40] text-white p-5 sm:p-6 ring-1 ring-[#4FBCC7]/25 hover:ring-[#4FBCC7]/55 transition-all"
      >
        <div className="inline-flex items-center gap-2 font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[#4FBCC7] font-bold mb-2">
          <Headphones className="h-3 w-3" />
          Free · while you&rsquo;re here
        </div>
        <h3 className="font-heading text-[1.2rem] font-bold tracking-tight leading-[1.15] mb-2">
          The Locker Room
        </h3>
        <p className="text-[12.5px] text-white/75 leading-snug mb-3">
          {audience === 'athlete'
            ? 'Pre-game playlists + the Apex Six notecards. Free.'
            : audience === 'parent'
              ? 'Hear the playlists your athlete uses to lock in pre-game.'
              : 'The mental prep audio library your team can use today.'}
        </p>
        <span className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#4FBCC7] group-hover:text-white transition-colors">
          Open the locker
          <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </a>
    </div>
  );
};

const StepResult = ({ winner, audience, archetypes, audienceConfig }) => {
  const arche = archetypes[winner];
  const Icon = ARCHETYPE_ICONS[winner] || Trophy;

  if (!arche) return null;
  return (
    <div className="space-y-6" data-testid="quiz-result">
      <div className="rounded-2xl bg-gradient-to-br from-[#004C54] to-[#003a40] text-white p-6 sm:p-8">
        <div className="flex items-center gap-2 text-[#4FBCC7] text-xs font-bold uppercase tracking-[0.18em]">
          <Sparkles className="h-3 w-3" /> Your {audience === 'athlete' ? 'mindset' : audience === 'parent' ? 'parenting' : 'coaching'} profile
        </div>
        <div className="flex items-start gap-4 mt-3">
          <div className="flex-shrink-0 w-16 h-16 rounded-xl bg-white/15 flex items-center justify-center">
            <Icon className="h-9 w-9" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-3xl sm:text-4xl font-bold tracking-tight" data-testid="quiz-result-name">{arche.name}</div>
            <div className="text-white/85 mt-1 text-base sm:text-lg italic">{arche.headline}</div>
          </div>
        </div>
        <p className="text-white/85 mt-5 leading-relaxed text-[15px] sm:text-base">{arche.description}</p>
      </div>

      <Card className="border-2 border-[#004C54]/20">
        <CardContent className="p-6 sm:p-7">
          <div className="text-xs font-bold text-[#004C54] uppercase tracking-wider">Your top gap · what to run first</div>
          <div className="font-heading text-2xl sm:text-3xl font-bold tracking-tight mt-1">
            {arche.framework_name}
          </div>
          <div className="inline-block mt-2 px-2.5 py-1 rounded-full bg-[#4FBCC7] text-white text-[10px] font-bold uppercase tracking-wider">
            {arche.framework_tag}
          </div>
          <p className="text-foreground/80 mt-3 leading-relaxed">{arche.framework_pitch}</p>
          <p className="text-[#004C54] font-semibold italic mt-3">{arche.growth_line}</p>
        </CardContent>
      </Card>

      {/* Confirmation that we've emailed the profile (the gate already
          captured email upstream). */}
      <Card className="border-2 border-[#004C54]/30 bg-[#004C54]/5">
        <CardContent className="p-6 sm:p-7 text-center space-y-3" data-testid="quiz-email-sent">
          <div className="w-12 h-12 mx-auto rounded-full bg-[#004C54]/15 flex items-center justify-center">
            <CheckCircle2 className="h-6 w-6 text-[#004C54]" />
          </div>
          <div className="font-heading text-xl font-bold">Sent to your inbox.</div>
          <p className="text-sm text-foreground/70">Your full profile + the playbook PDF are on the way. (1–2 minutes — check spam if it lags.)</p>
          <a
            href={audienceConfig.pdf}
            download={audienceConfig.pdfFilename}
            data-testid="quiz-download-pdf-now"
            className="inline-block"
          >
            <Button variant="outline" className="border-[#004C54] text-[#004C54] hover:bg-[#004C54]/10">
              <Download className="h-4 w-4 mr-2" /> {audienceConfig.pdfCta}
            </Button>
          </a>
        </CardContent>
      </Card>

      <div className="rounded-xl border border-[#004C54]/20 p-5 text-center">
        <div className="font-heading text-lg font-bold">Want a coach in your pocket?</div>
        <p className="text-foreground/70 text-sm mt-1 mb-4">
          AI Coach trained on these exact frameworks. All in Coach Lewis&rsquo;s voice. Try free.
        </p>
        <a href="/app/signup" data-testid="quiz-cta-signup">
          <Button size="lg" className="bg-[#004C54] hover:bg-[#003a40] text-white">
            Try AI Coach free <ExternalLink className="h-3.5 w-3.5 ml-2" />
          </Button>
        </a>
      </div>

      {/*
        Audience-specific next-step CTA — Phase 21.2
        Promotes the highest-intent paid product right after the archetype
        reveal (the moment of peak engagement). Athletes get the $27 IDP
        upsell; parents get a Discovery Call; coaches get the Workshop
        inquiry. Locker Room is offered to all audiences as a soft
        secondary "while you're here" CTA.
      */}
      <NextStepCTA audience={audience} />

      {/* Cross-pollination — Phase 33. Tease the Mental Performance Checklist
          to athletes who came in via the quiz, so the two lead-gen funnels
          feed each other instead of competing. */}
      {audience === 'athlete' && (
        <a
          href="/checklist"
          data-testid="quiz-cross-checklist"
          className="block rounded-xl border border-[#004C54]/15 bg-[#F5F3EE] hover:bg-[#efece5] p-4 transition-colors"
        >
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-[#004C54] text-[#4FBCC7] flex items-center justify-center flex-shrink-0">
              <Download className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono-accent uppercase tracking-[0.18em] font-bold text-[#004C54]">
                Bonus
              </div>
              <div className="text-[14px] font-semibold text-[#0a0d0e]">
                Grab the Pre-Game Mental Performance Checklist (free PDF) <span aria-hidden="true">→</span>
              </div>
            </div>
          </div>
        </a>
      )}

      <div className="text-center text-xs text-foreground/55">
        Took it for someone else? <a href="/quiz" className="underline hover:text-[#004C54]" data-testid="quiz-restart">Retake the quiz →</a>
      </div>
    </div>
  );
};

// ---------- Page ------------------------------------------------------------

export default function MindsetQuiz() {
  const navigate = useNavigate();

  // step: 'intro' | 'audience' | 'sport' | 'q0'..'q7' | 'gate' | 'result'
  const [step, setStep] = useState('intro');
  const [audience, setAudience] = useState(null);
  const [sport, setSport] = useState(null);
  const [answers, setAnswers] = useState([]); // archetypes per question
  const [submitting, setSubmitting] = useState(false);
  // `sent` is now implicit: once gate -> result transitions happen, the email
  // has already been captured. Kept as a guard to prevent double-submits.
  const [sent, setSent] = useState(false);

  // Deep-link support — `/quiz?audience=athlete|parent|coach` jumps straight
  // past the intro + audience picker. Used by the homepage audience cards so
  // each path feels purpose-built. Sport is still picked if athlete.
  // Read the initial URL once; later quiz state changes must not restart the quiz.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const a = (params.get('audience') || '').toLowerCase();
    if (a && AUDIENCE_CONFIG[a]) {
      setAudience(a);
      setAnswers([]);
      setSport(null);
      const needsSport = AUDIENCE_CONFIG[a].data?.REQUIRES_SPORT !== false;
      setStep(needsSport ? 'sport' : 'q0');
    }
    // We only want this to run once on mount.
  }, []);

  const audienceConfig = audience ? AUDIENCE_CONFIG[audience] : null;
  const audienceData = audienceConfig?.data;
  const questions = audienceData?.QUESTIONS || [];
  const archetypes = audienceData?.ARCHETYPES || {};
  const TOTAL_Q = questions.length;
  const requiresSport = audienceData?.REQUIRES_SPORT !== false; // default true for athlete

  const qIndex = useMemo(() => {
    if (!step?.startsWith?.('q')) return -1;
    return parseInt(step.slice(1), 10);
  }, [step]);

  const { winner } = useMemo(() => {
    if (!audienceData || answers.length !== TOTAL_Q || TOTAL_Q === 0) return { winner: null };
    return audienceData.scoreQuiz(answers);
  }, [answers, TOTAL_Q, audienceData]);

  const goStart = () => setStep('audience');

  const goAudience = (a) => {
    setAudience(a);
    setAnswers([]);
    setSport(null);
    const cfg = AUDIENCE_CONFIG[a];
    const needsSport = cfg?.data?.REQUIRES_SPORT !== false;
    setStep(needsSport ? 'sport' : 'q0');
  };

  const goSport = (s) => { setSport(s); setStep('q0'); };

  const goAnswer = (archetypeKey) => {
    const next = [...answers, archetypeKey];
    setAnswers(next);
    if (next.length >= TOTAL_Q) {
      // Phase 33 — route through the Results Gate (email capture) before
      // revealing the archetype + framework. The gate calls submitEmail()
      // which advances to 'result' on success.
      setStep('gate');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setStep(`q${next.length}`);
    }
  };

  const goBack = () => {
    if (step === 'audience') return setStep('intro');
    if (step === 'sport') return setStep('audience');
    if (step === 'q0') return setStep(requiresSport ? 'sport' : 'audience');
    if (step?.startsWith?.('q')) {
      const idx = parseInt(step.slice(1), 10);
      setAnswers((prev) => prev.slice(0, idx));
      return setStep(`q${idx - 1}`);
    }
    if (step === 'gate') {
      // Let users adjust their last answer before committing email.
      setAnswers((prev) => prev.slice(0, -1));
      return setStep(`q${TOTAL_Q - 1}`);
    }
    if (step === 'result') {
      // Result is post-submit; "back" here just returns to the gate-style
      // confirmation. Leaving answers intact so they can re-share email if
      // they want, but we don't reset 'sent' so no double-submission.
      return setStep('gate');
    }
  };

  const submitEmail = async ({ email, name }) => {
    if (sent) {
      // Already submitted once — just advance to results (defensive guard).
      setStep('result');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setSubmitting(true);
    try {
      const params = new URLSearchParams(window.location.search);
      await api.post('/api/quiz/submit', {
        email, name,
        audience,
        sport: sport || 'general',
        answers,
        archetype: winner,
        source: params.get('src') || 'quiz',
      });
      setSent(true);
      toast.success('Score unlocked. Check your inbox for the playbook.');
      setStep('result');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) {
      toast.error(e?.message || 'Could not unlock results. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // Resolve the scene + stem + options for the current question. Athlete
  // questions pull a sport-scoped scene; parent/coach questions carry their
  // own scene baked into the question.
  let body = null;
  if (step === 'intro') {
    body = <StepIntro onStart={goStart} />;
  } else if (step === 'audience') {
    body = <StepAudience value={audience} onChoose={goAudience} onBack={() => navigate('/')} />;
  } else if (step === 'sport') {
    body = <StepSport value={sport} onChoose={goSport} onBack={goBack} />;
  } else if (qIndex >= 0 && qIndex < TOTAL_Q) {
    const q = questions[qIndex];
    const scene = audience === 'athlete'
      ? getScene(sport, q.id, 'athlete')
      : q.scene;
    body = (
      <StepQuestion
        index={qIndex}
        total={TOTAL_Q}
        scene={scene}
        stem={q.stem}
        options={q.options}
        onAnswer={goAnswer}
        onBack={goBack}
      />
    );
  } else if (step === 'gate' && winner) {
    body = (
      <StepGate
        audience={audience}
        submitting={submitting}
        onSubmit={submitEmail}
        onBack={goBack}
      />
    );
  } else if (step === 'result' && winner) {
    body = (
      <StepResult
        winner={winner}
        audience={audience}
        archetypes={archetypes}
        audienceConfig={audienceConfig}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="mindset-quiz">
      <div className="max-w-2xl mx-auto px-4 py-10 sm:py-14">{body}</div>
    </div>
  );
}
